// =============================================================================
// SinoNotificacoes - Central de Comunicação (Fase B): sino do topbar
// =============================================================================
// Badge de não-lidas + dropdown com as notificações do usuário logado (hoje só
// menções em comentários). Clicar numa notificação marca como lida e abre a
// operação na aba Comentários. Sem tempo real: busca ao montar + poll leve.
// =============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { notificacoesApi } from '../services/api';
import type { Notificacao } from '../types';

const POLL_MS = 60_000;

function quando(iso: string): string {
  const d = new Date(iso);
  const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
  if (diffMin < 1) return 'agora';
  if (diffMin < 60) return `há ${diffMin} min`;
  if (diffMin < 1440) return `há ${Math.floor(diffMin / 60)} h`;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

export default function SinoNotificacoes() {
  const navigate = useNavigate();
  const [contagem, setContagem] = useState(0);
  const [aberto, setAberto] = useState(false);
  const [itens, setItens] = useState<Notificacao[]>([]);
  const [carregando, setCarregando] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const atualizarContagem = useCallback(() => {
    notificacoesApi.contagem().then(setContagem).catch(() => { /* silencioso */ });
  }, []);

  useEffect(() => {
    atualizarContagem();
    const t = setInterval(atualizarContagem, POLL_MS);
    // Sinaliza na hora: outra parte do app (ex.: comentar com menção) pede refetch;
    // e ao voltar o foco pra aba, revalida sem esperar o poll.
    const aoFocar = () => { if (document.visibilityState === 'visible') atualizarContagem(); };
    window.addEventListener('notificacoes:atualizar', atualizarContagem);
    document.addEventListener('visibilitychange', aoFocar);
    window.addEventListener('focus', atualizarContagem);
    return () => {
      clearInterval(t);
      window.removeEventListener('notificacoes:atualizar', atualizarContagem);
      document.removeEventListener('visibilitychange', aoFocar);
      window.removeEventListener('focus', atualizarContagem);
    };
  }, [atualizarContagem]);

  const abrir = async () => {
    const vai = !aberto;
    setAberto(vai);
    if (vai) {
      setCarregando(true);
      try {
        setItens(await notificacoesApi.listar());
      } catch {
        setItens([]);
      } finally {
        setCarregando(false);
      }
    }
  };

  const aoClicar = async (n: Notificacao) => {
    if (!n.lidaEm) {
      try {
        await notificacoesApi.marcarLida(n.id);
        setItens((prev) => prev.map((x) => (x.id === n.id ? { ...x, lidaEm: new Date().toISOString() } : x)));
        setContagem((c) => Math.max(0, c - 1));
      } catch { /* segue mesmo assim */ }
    }
    setAberto(false);
    if (n.operacaoId) navigate(`/operacoes/${n.operacaoId}?aba=comentarios`);
  };

  const lerTodas = async () => {
    try {
      await notificacoesApi.lerTodas();
      const agora = new Date().toISOString();
      setItens((prev) => prev.map((x) => (x.lidaEm ? x : { ...x, lidaEm: agora })));
      setContagem(0);
    } catch { /* silencioso */ }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={abrir}
        className="relative p-2 rounded-lg hover:bg-otm-subtle text-otm-txt-2 transition-colors"
        title="Notificações"
      >
        <Bell size={20} />
        {contagem > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center text-[10px] font-semibold text-white bg-otm-orange rounded-full">
            {contagem > 9 ? '9+' : contagem}
          </span>
        )}
      </button>

      {aberto && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setAberto(false)} />
          <div className="absolute right-0 mt-1 w-80 max-w-[90vw] bg-white rounded-xl shadow-lg border border-otm-border z-50 overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 border-b border-otm-border">
              <span className="text-sm font-semibold text-otm-txt-1">Avisos</span>
              {itens.some((n) => !n.lidaEm) && (
                <button onClick={lerTodas} className="text-xs text-otm-navy hover:underline">
                  Marcar todas como lidas
                </button>
              )}
            </div>

            <div className="max-h-96 overflow-y-auto">
              {carregando ? (
                <p className="text-sm text-slate-400 text-center py-6">Carregando…</p>
              ) : itens.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">Nenhum aviso por aqui.</p>
              ) : (
                itens.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => aoClicar(n)}
                    className={`w-full flex gap-2 px-3 py-2.5 text-left border-b border-otm-border/60 last:border-b-0 transition-colors hover:bg-otm-subtle ${
                      n.lidaEm ? '' : 'bg-otm-navy/5'
                    }`}
                  >
                    <span className={`shrink-0 mt-1.5 w-2 h-2 rounded-full ${n.lidaEm ? 'bg-transparent' : 'bg-otm-orange'}`} />
                    <span className="min-w-0">
                      <span className="block text-sm text-otm-txt-1 leading-snug">{n.resumo}</span>
                      <span className="block text-[11px] text-otm-txt-3 mt-0.5">{quando(n.criadoEm)}</span>
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
